import { Anchor, Button, Group, Select, Spoiler, Stack, Text, Typography } from "@mantine/core";
import { useField } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconChevronDown, IconChevronUp, IconCopy, IconEdit, IconMailFast, IconTrash } from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { DetailCard } from "shared/components/global/DetailCard";
import { TemplateSearchRepository } from "shared/components/message/templates/data";
import { TemplateTypeBadge } from "shared/components/message/templates/TemplateTypeBadge";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePromise } from "shared/hooks/usePromise";
import { permissionsCheck, usePermissionCheck, usePermissions } from "shared/stores/oidc";
import { titlecase, voiding } from "shared/utils/fns";
import { mdToHtml } from "shared/utils/md";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { MessageSeverity, Permission, TemplateType } from "src/gql/graphql";
import { container } from "tsyringe";

type TemplateCardProps = {
    template: {
        id: string;
        type: TemplateType;
        owner: {
            id: string;
            name: string;
        };
        message: {
            subject: string;
            severity: MessageSeverity;
            richBody: string;
        };
    };
};

export const TemplateCard = ({ template }: TemplateCardProps) => {
    const nav = useNavigate();
    const { permissions } = usePermissions();

    const hasTemplateCreate = usePermissionCheck(Permission.GroupTemplateCreate, template.owner.id);
    const hasTemplateUpdate = usePermissionCheck(Permission.GroupTemplateUpdate, template.owner.id);
    const hasTemplateArchive = usePermissionCheck(Permission.GroupTemplateDelete, template.owner.id);

    const duplicateTemplate = useGraphqlMutation(mutations.duplicate);
    const archiveTemplate = useGraphqlMutation(mutations.archive);

    const repo = container.resolve(TemplateSearchRepository);

    const __html = usePromise(() => mdToHtml(template.message.richBody), [template.message.richBody]);

    function handleDuplicate(id: string) {
        duplicateTemplate
            .mutateAsync({ id })
            .then((t) => repo.upsert(t.duplicateTemplate))
            .then(() => qk.invalidate("templates"))
            .then(() => notify.show.success("Duplicated Template"))
            .catch(notify.catch);
    }

    function handleArchive(id: string) {
        modals.openConfirmModal({
            title: "Archive Template",
            children: <Text size="sm">Are you sure you want to archive this template?</Text>,
            labels: {
                confirm: "Archive",
                cancel: "Cancel",
            },
            onConfirm: () =>
                void archiveTemplate
                    .mutateAsync({ id })
                    .then(() => repo.remove(id))
                    .then(() => qk.invalidate("templates"))
                    .then(() => notify.show.success("Archived Template"))
                    .catch(notify.catch),
        });
    }

    function handlePromote(id: string, initialValue: TemplateType) {
        function Panel() {
            const field = useField({ initialValue });
            const setTemplateType = useGraphqlMutation(mutations.support.setTemplateType);

            return (
                <Stack>
                    <Select label="Template Type" data={Object.values(TemplateType)} {...field.getInputProps()} />
                    <Group justify="flex-end">
                        <Button
                            onClick={voiding(async () => {
                                await setTemplateType
                                    .mutateAsync({ id, type: field.getValue() })
                                    .then((t) => repo.upsert(t.support.setTemplateType))
                                    .then(() => qk.invalidate("templates").catch(notify.catch))
                                    .then(modals.closeAll)
                                    .catch(notify.catch);
                            })}
                            loading={setTemplateType.isPending}
                        >
                            Submit
                        </Button>
                    </Group>
                </Stack>
            );
        }

        modals.open({
            title: "Promote Template",
            children: <Panel />,
        });
    }

    return (
        <DetailCard
            key={template.id}
            title={template.message.subject}
            subtitle={
                <Text size="sm">
                    in{" "}
                    {permissionsCheck(permissions, [
                        [Permission.GroupSenderManage, template.owner.id],
                        [Permission.GroupUsersView, template.owner.id],
                    ]) ? (
                        <Link to="/organization/groups/$groupId" params={{ groupId: template.owner.id }}>
                            <Anchor>{template.owner.name}</Anchor>
                        </Link>
                    ) : (
                        template.owner.name
                    )}
                    {template.message.severity == MessageSeverity.Urgent && ` | ${titlecase(template.message.severity)}`}
                </Text>
            }
            badge={<TemplateTypeBadge type={template.type} />}
            rightSection={
                <Link to="/messages/templates/send/$id" params={{ id: template.id }}>
                    <Button size="compact-sm" variant="default" leftSection={<IconMailFast size={16} />} component="span">
                        Send
                    </Button>
                </Link>
            }
            actions={[
                hasTemplateUpdate
                    ? {
                          icon: <IconEdit size={16} />,
                          label: "Edit",
                          onClick: () => nav({ to: "/messages/templates/$id", params: { id: template.id } }),
                      }
                    : null,
                template.type == TemplateType.Customer && hasTemplateCreate
                    ? { icon: <IconCopy size={16} />, label: "Duplicate", onClick: () => handleDuplicate(template.id) }
                    : null,
                template.type == TemplateType.Customer && hasTemplateArchive
                    ? { icon: <IconTrash size={16} />, label: "Archive", onClick: () => handleArchive(template.id), color: "red" }
                    : null,
            ].filter((it) => !!it)}
            supportActions={[{ label: "Promote Template", onClick: () => handlePromote(template.id, template.type) }]}
        >
            <Spoiler
                maxHeight={80}
                styles={{ control: { width: "100%" } }}
                showLabel={
                    <>
                        <Group w="100%" justify="center" align="center" gap="2px">
                            <Button size="compact-xs" variant="transparent" component="a" rightSection={<IconChevronDown size={16} />}>
                                More
                            </Button>
                        </Group>
                    </>
                }
                hideLabel={
                    <Group w="100%" justify="center" align="center" gap="2px">
                        <Button size="compact-xs" variant="transparent" component="a" rightSection={<IconChevronUp size={16} />}>
                            Less
                        </Button>
                    </Group>
                }
            >
                <Typography>
                    <span dangerouslySetInnerHTML={{ __html }} />
                </Typography>
            </Spoiler>
        </DetailCard>
    );
};

const mutations = {
    duplicate: graphql(`
        mutation TemplateListDuplicateTemplate($id: UUID!) {
            duplicateTemplate(id: $id) {
                ...TemplateLike
            }
        }
    `),
    archive: graphql(`
        mutation TemplateListArchiveTemplate($id: UUID!) {
            archiveTemplate(id: $id) {
                ...TemplateLike
            }
        }
    `),
    support: {
        setTemplateType: graphql(`
            mutation TemplateListSetTemplateType($id: UUID!, $type: TemplateType!) {
                support {
                    setTemplateType(params: { id: $id, type: $type }) {
                        ...TemplateLike
                    }
                }
            }
        `),
    },
};
